// Generated from scripts/paginated-list.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { getScrollContainer } from '@theme/scroll-container';
import { prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
const PAGINATION_TYPES = ['numbered', 'load_more', 'infinite'];
const DIRECTIONS = ['next', 'previous'];
const LINK_REFS = { next: 'nextLink', previous: 'previousLink' };
/** A result of the list: counted, tracked for the address and announced. */
const CARD_SELECTOR = ':scope > [ref="cards[]"]';
/**
 * A grid cell that is not a result, such as a promotion tile. Each page's response carries its own tile at
 * its own index, so a tile travels with its page but is never counted as a result.
 */
const TILE_SELECTOR = ':scope > [data-grid-tile]';
const PAGE_PARAMETER = 'page';
/** A card counts as in view once this much of it shows; slivers at the viewport edges do not move the address. */
const IN_VIEW_RATIO = 0.5;
function isPaginationType(value) {
    return value !== null && PAGINATION_TYPES.includes(value);
}
function pageOf(card) {
    const page = Number(card.dataset[PAGE_PARAMETER]);
    return Number.isInteger(page) && page > 0 ? page : undefined;
}
/**
 * Adds the previous or next page of results to the grid in place.
 *
 * The server renders real links to both neighbouring pages, so crawlers and visitors without
 * JavaScript page through the list natively. This element turns those links into in-place loads,
 * and in the `infinite` type also follows the next link when it nears the viewport.
 *
 * The address bar follows the earliest page substantially in view, in both scroll directions, so
 * a reload or a shared link reopens the results the shopper was looking at.
 *
 * A page also adds its tiles (`data-grid-tile`, such as a promotion tile) to the grid. The server renders
 * each tile inside the page that shows it, at its own index, so the list only moves it along with the
 * cards; the cards alone are the results that the address tracking, the status text and the empty-page
 * check count.
 */
export default class PaginatedList extends Component {
    #lifetime;
    #requests = new AbortController();
    #pages = new Map();
    #state = { status: 'idle' };
    #observer;
    #pageObserver;
    #inView = new Set();
    #aspectRatio;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = new AbortController();
        const { signal } = this.#lifetime;
        this.addEventListener('click', this.#onClick, { signal });
        this.addEventListener('pointerover', this.#onIntent, { signal });
        this.addEventListener('focusin', this.#onIntent, { signal });
        const templateCard = this.querySelector('[ref="cardGallery"]');
        if (templateCard instanceof HTMLElement)
            this.#aspectRatio = new PaginatedListAspectRatioHelper({ templateCard });
        this.#start();
    }
    /** A section morph (filters, sorting, editor change) replaced the first page: drop all loaded state. */
    updatedCallback() {
        super.updatedCallback();
        this.#reset();
        this.#start();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#reset();
    }
    get sectionId() {
        const id = this.getAttribute('section-id');
        if (!id)
            throw new Error('The section-id attribute is required');
        return id;
    }
    get paginationType() {
        const value = this.getAttribute('pagination-type');
        return isPaginationType(value) ? value : 'numbered';
    }
    #start() {
        if (this.paginationType === 'numbered')
            return;
        this.#trackPages();
        this.#observeNext();
        // Scrolling will reach the next page, so fetch it while idle; a button is prefetched on intent instead.
        if (this.paginationType === 'infinite')
            this.#prefetch('next');
    }
    #reset() {
        this.#requests.abort();
        this.#requests = new AbortController();
        this.#pages.clear();
        this.#state = { status: 'idle' };
        this.#observer?.disconnect();
        this.#observer = undefined;
        this.#pageObserver?.disconnect();
        this.#pageObserver = undefined;
        this.#inView.clear();
    }
    #link(direction) {
        const link = this.refs[LINK_REFS[direction]];
        return link instanceof HTMLAnchorElement && link.isConnected ? link : undefined;
    }
    #directionOf(target) {
        if (!(target instanceof Element))
            return undefined;
        const anchor = target.closest('a');
        return anchor ? DIRECTIONS.find(direction => anchor === this.#link(direction)) : undefined;
    }
    get #grid() {
        const grid = this.refs.grid;
        return grid instanceof HTMLElement ? grid : undefined;
    }
    get #status() {
        const status = this.refs.paginationStatus;
        return status instanceof HTMLElement ? status : undefined;
    }
    #cards(grid) {
        return [...grid.querySelectorAll(CARD_SELECTOR)].filter((card) => card instanceof HTMLElement);
    }
    /** The cards and tiles of a grid in document order. */
    #items(grid) {
        return [...grid.querySelectorAll(`${CARD_SELECTOR}, ${TILE_SELECTOR}`)].filter((item) => item instanceof HTMLElement);
    }
    #onClick = (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
        const direction = this.#directionOf(event.target);
        if (!direction)
            return;
        event.preventDefault();
        void this.#load(direction, 'user');
    };
    /** Hovering or focusing a page link fetches its page, so the click that follows renders from cache. */
    #onIntent = (event) => {
        const direction = this.#directionOf(event.target);
        if (direction)
            this.#prefetch(direction);
    };
    #observeNext() {
        this.#observer?.disconnect();
        const next = this.#link('next');
        if (this.paginationType !== 'infinite' || !next)
            return;
        this.#observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting))
                void this.#load('next', 'scroll');
        }, { rootMargin: '0px 0px 600px 0px' });
        // Observing again also re-checks a link that stayed in view after a short page was appended.
        this.#observer.observe(next);
    }
    /** Watches every card so the address can follow the page in view; rebuilt after each load. */
    #trackPages() {
        this.#pageObserver?.disconnect();
        this.#inView.clear();
        const grid = this.#grid;
        if (!grid)
            return;
        this.#pageObserver ??= new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!(entry.target instanceof HTMLElement))
                    continue;
                if (entry.isIntersecting && entry.intersectionRatio >= IN_VIEW_RATIO)
                    this.#inView.add(entry.target);
                else
                    this.#inView.delete(entry.target);
            }
            this.#syncAddress();
        }, { threshold: IN_VIEW_RATIO });
        for (const card of this.#cards(grid))
            this.#pageObserver.observe(card);
    }
    /** Writes the earliest page substantially in view into the address, replacing the current entry. */
    #syncAddress() {
        if (Shopify.designMode)
            return;
        let page;
        for (const card of this.#inView) {
            const candidate = pageOf(card);
            if (candidate !== undefined && (page === undefined || candidate < page))
                page = candidate;
        }
        // Nothing substantially in view (header, footer) keeps the last page written.
        if (page === undefined)
            return;
        const url = new URL(location.href);
        if (Number(url.searchParams.get(PAGE_PARAMETER) ?? 1) === page)
            return;
        if (page === 1)
            url.searchParams.delete(PAGE_PARAMETER);
        else
            url.searchParams.set(PAGE_PARAMETER, String(page));
        try {
            history.replaceState(history.state, '', url);
        }
        catch {
            // Safari limits replaceState calls per interval; the next page change writes again.
        }
    }
    async #load(direction, trigger) {
        // A failed automatic load waits for the shopper's retry instead of looping on every intersection.
        if (this.#state.status === 'loading' || (this.#state.status === 'error' && trigger === 'scroll'))
            return;
        const link = this.#link(direction);
        const grid = this.#grid;
        if (!link || !grid)
            return;
        const url = new URL(link.href);
        const { signal } = this.#requests;
        this.#render({ status: 'loading', direction });
        let page;
        try {
            page = this.#parse(await this.#fetch(url));
        }
        catch {
            if (signal.aborted)
                return;
            this.#pages.delete(url.href);
            this.#render({ status: 'error', direction });
            return;
        }
        if (signal.aborted)
            return;
        // Content added above the viewport must not move what the shopper is looking at.
        const anchor = direction === 'previous' ? grid.firstElementChild : null;
        const anchorTop = anchor?.getBoundingClientRect().top;
        if (direction === 'next')
            grid.append(...page.items);
        else
            grid.prepend(...page.items);
        this.#aspectRatio?.processNewElements();
        const following = direction === 'next' ? page.next : page.previous;
        if (following)
            link.href = following;
        else
            (link.closest('[data-pagination-control]') ?? link).remove();
        if (anchor && anchorTop !== undefined) {
            const shift = anchor.getBoundingClientRect().top - anchorTop;
            if (shift)
                getScrollContainer().scrollBy({ top: shift, behavior: 'instant' });
        }
        this.#render({ status: 'idle' });
        this.#announce(grid);
        this.#trackPages();
        // Focus lands on the loaded card nearest the control the shopper used, revealing at most that card.
        if (trigger === 'user') {
            const nearest = direction === 'next' ? page.cards[0] : page.cards[page.cards.length - 1];
            nearest?.querySelector('a[href]')?.focus({ preventScroll: true });
            nearest?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
        }
        if (direction === 'next') {
            this.#observeNext();
            if (this.paginationType === 'infinite')
                this.#prefetch('next');
        }
    }
    #fetch(url) {
        const cached = this.#pages.get(url.href);
        if (cached)
            return cached;
        // Our own cache keeps one request per page; the renderer's cache would also keep failed responses.
        // The renderer writes section_id into the URL it receives, so it gets a copy.
        const request = sectionRenderer.getSectionHTML(this.sectionId, false, new URL(url), this.#requests.signal);
        this.#pages.set(url.href, request);
        request.catch(() => this.#pages.delete(url.href));
        return request;
    }
    #prefetch(direction) {
        const link = this.#link(direction);
        if (!link)
            return;
        const url = new URL(link.href);
        if (this.#pages.has(url.href))
            return;
        requestIdleCallback(() => {
            if (this.#link(direction)?.href === url.href)
                void this.#fetch(url).catch(() => undefined);
        });
    }
    /** Validates a rendered section: a response without result cards is an error, never an empty page. */
    #parse(html) {
        const rendered = new DOMParser().parseFromString(html, 'text/html');
        const grid = rendered.querySelector('[ref="grid"]');
        if (!(grid instanceof HTMLElement))
            throw new Error('The rendered page has no result cards');
        const cards = this.#cards(grid);
        if (!cards.length)
            throw new Error('The rendered page has no result cards');
        return {
            items: this.#items(grid),
            cards,
            next: rendered.querySelector(`[ref="${LINK_REFS.next}"]`)?.getAttribute('href') ?? null,
            previous: rendered.querySelector(`[ref="${LINK_REFS.previous}"]`)?.getAttribute('href') ?? null,
        };
    }
    #render(state) {
        const previous = this.#state;
        this.#state = state;
        const grid = this.#grid;
        switch (state.status) {
            case 'idle': {
                grid?.removeAttribute('aria-busy');
                if (previous.status !== 'idle')
                    this.#label(previous.direction, 'label');
                break;
            }
            case 'loading': {
                grid?.setAttribute('aria-busy', 'true');
                this.#label(state.direction, 'loadingLabel');
                this.#link(state.direction)?.setAttribute('aria-disabled', 'true');
                break;
            }
            case 'error': {
                grid?.removeAttribute('aria-busy');
                this.#label(state.direction, 'retryLabel');
                const status = this.#status;
                if (status?.dataset.errorMessage)
                    status.textContent = status.dataset.errorMessage;
                break;
            }
            default: {
                const exhaustive = state;
                return exhaustive;
            }
        }
    }
    #label(direction, key) {
        const link = this.#link(direction);
        if (!link)
            return;
        const text = link.dataset[key];
        if (text)
            link.textContent = text;
        if (key !== 'loadingLabel')
            link.removeAttribute('aria-disabled');
    }
    /** Updates the polite status region with the confirmed number of results now shown. */
    #announce(grid) {
        const status = this.#status;
        const template = status?.dataset.progress;
        if (!status || !template)
            return;
        status.textContent = template.replace('[shown]', String(this.#cards(grid).length));
    }
}
